import { createContext, useContext, useEffect, useState } from "react";
import { useSubjects } from "../../../../../../../../hooks/useSubjects";
import {
  Contacts,
  PaymentMethod,
  PersonalData,
  ReadOnlySubject,
} from "../../../../../../../../redux/apiSpecifications/apiSubjects";
import { Address } from "../../../../../../../../redux/apiSpecifications/apiSubjects";

export interface PolicySubjectDetailsContextData {
  subject?: ReadOnlySubject;
  status?: "idle" | "loading" | "refreshing" | "saving" | undefined;
  onSaveSubjectPersonalDataPromise: (
    subjectId: number,
    personalData: PersonalData
  ) => Promise<PersonalData>;
  onSaveSubjectContactsPromise: (
    subjectId: number,
    contacts: Contacts
  ) => Promise<Contacts>;
  onSaveSubjectAddressPromise: (address: Address) => Promise<Address[]>;
  onDeleteSubjectAddressPromise: (addressId: number) => Promise<Address[]>;
  onSaveSubjectPaymentMethodPromise: (
    subjectPaymentMethod: PaymentMethod
  ) => Promise<any>;
  onDeleteSubjectPaymentMethodPromise: (
    paymentMethodId: number
  ) => Promise<Address[]>;
}

type PolicySubjectDetailsState = {
  subject?: ReadOnlySubject;
  status?: "idle" | "loading" | "refreshing" | "saving" | undefined;
};

const PolicySubjectDetailsContext = createContext<
  PolicySubjectDetailsContextData | undefined
>(undefined);

interface IPolicySubjectDetailsProviderProps {
  idSubject: number;
  children: React.ReactNode;
}

const SubjectDetailsProvider: React.FC<IPolicySubjectDetailsProviderProps> = ({
  idSubject,
  children,
}) => {
  const [state, setState] = useState<PolicySubjectDetailsState>({});

  const {
    getSubjectPromise,
    updateSubjectPersonalDataPromise,
    updatePlainSubjectContactsPromise,
    createSubjectAddressPromise,
    updateSubjectAddressPromise,
    deleteSubjectAddressPromise,
    createSubjectPaymentMethodPromise,
    updateSubjectPaymentMethodPromise,
    deleteSubjectPaymentMethodPromise,
  } = useSubjects();

  useEffect(() => {
    (async () => {
      setState({
        ...state,
        status: "loading",
      });

      getSubjectPromise(idSubject)
        .then((subject) => {
          setState({
            ...state,
            subject: subject,
            status: "idle",
          });
        })
        .catch((error) => {
          setState({
            ...state,
            subject: undefined,
            status: "idle",
          });
        });
    })();
  }, [idSubject]);

  const onSaveSubjectPersonalDataPromise = (
    subjectId: number,
    personalData: PersonalData
  ): Promise<PersonalData> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      updateSubjectPersonalDataPromise(subjectId, personalData)
        .then((personalData) => {
          setState({
            ...state,
            subject: {
              ...state!.subject!,
              personalData: personalData,
            },
            status: "idle",
          });

          resolve(personalData);
        })
        .catch((reason) => {
          setState({
            ...state,
            status: "idle",
          });

          reject(reason);
        });
    });

  const onSaveSubjectContactsPromise = (
    subjectId: number,
    contacts: Contacts
  ): Promise<Contacts> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      updatePlainSubjectContactsPromise(subjectId, contacts)
        .then((contacts) => {
          setState({
            ...state,
            subject: {
              ...state!.subject!,
              contacts: contacts,
            },
            status: "idle",
          });

          resolve(contacts);
        })
        .catch((reason) => {
          setState({
            ...state,
            status: "idle",
          });

          reject(reason);
        });
    });

  const onSaveSubjectAddressPromise = (address: Address): Promise<Address[]> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      if (address.id) {
        updateSubjectAddressPromise(state.subject?.id!, address)
          .then((subjectAddresses) => {
            setState({
              ...state,
              subject: {
                ...state!.subject!,
                addresses: subjectAddresses,
              },
              status: "idle",
            });

            resolve(subjectAddresses);
          })
          .catch((reason) => {
            setState({
              ...state,
              status: "idle",
            });

            reject(reason);
          });
      } else {
        createSubjectAddressPromise(state.subject?.id!, address)
          .then((subjectAddresses) => {
            setState({
              ...state,
              subject: {
                ...state!.subject!,
                addresses: subjectAddresses,
              },
              status: "idle",
            });

            resolve(subjectAddresses);
          })
          .catch((reason) => {
            setState({
              ...state,
              status: "idle",
            });

            reject(reason);
          });
      }
    });

  const onDeleteSubjectAddressPromise = (
    addressId: number
  ): Promise<Address[]> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      deleteSubjectAddressPromise(state.subject?.id!, addressId)
        .then((subjectAddresses) => {
          setState({
            ...state,
            subject: {
              ...state!.subject!,
              addresses: subjectAddresses,
            },
            status: "idle",
          });

          resolve(subjectAddresses);
        })
        .catch((reason) => {
          setState({
            ...state,
            status: "idle",
          });

          reject(reason);
        });
    });

  const onSaveSubjectPaymentMethodPromise = (
    subjectPaymentMethod: PaymentMethod
  ): Promise<PaymentMethod[]> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      if (subjectPaymentMethod!.id) {
        updateSubjectPaymentMethodPromise(
          state.subject?.id!,
          subjectPaymentMethod!
        )
          .then((subjectPaymentMethods) => {
            setState({
              ...state,
              subject: {
                ...state!.subject!,
                paymentMethods: subjectPaymentMethods,
              },
              status: "idle",
            });

            resolve(subjectPaymentMethods);
          })
          .catch((reason) => {
            setState({
              ...state,
              status: "idle",
            });

            reject(reason);
          });
      } else {
        createSubjectPaymentMethodPromise(
          state.subject?.id!,
          subjectPaymentMethod!
        )
          .then((subjectPaymentMethods) => {
            setState({
              ...state,
              subject: {
                ...state!.subject!,
                paymentMethods: subjectPaymentMethods,
              },
              status: "idle",
            });

            resolve(subjectPaymentMethods);
          })
          .catch((reason) => {
            setState({
              ...state,
              status: "idle",
            });

            reject(reason);
          });
      }
    });

  const onDeleteSubjectPaymentMethodPromise = (
    paymentMethodId: number
  ): Promise<PaymentMethod[]> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      deleteSubjectPaymentMethodPromise(state.subject?.id!, paymentMethodId)
        .then((subjectPaymentMethods) => {
          setState({
            ...state,
            subject: {
              ...state!.subject!,
              paymentMethods: subjectPaymentMethods,
            },
            status: "idle",
          });

          resolve(subjectPaymentMethods);
        })
        .catch((reason) => {
          setState({
            ...state,
            status: "idle",
          });

          reject(reason);
        });
    });

  return (
    <PolicySubjectDetailsContext.Provider
      value={{
        status: state.status,
        subject: state.subject,
        onSaveSubjectPersonalDataPromise,
        onSaveSubjectContactsPromise,
        onSaveSubjectAddressPromise,
        onDeleteSubjectAddressPromise,
        onSaveSubjectPaymentMethodPromise,
        onDeleteSubjectPaymentMethodPromise,
      }}
    >
      {children}
    </PolicySubjectDetailsContext.Provider>
  );
};

export const useSubjectDetailsContext = (): PolicySubjectDetailsContextData => {
  const context = useContext(PolicySubjectDetailsContext);

  if (!context) {
    throw new Error("Errore");
  }

  return context;
};

export default SubjectDetailsProvider;
